import { MultiSelect, NumberInput, PasswordInput, Select, Switch, TagsInput, Textarea, TextInput } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { type Locale, labels, type ResolvedField, type Resource } from '@tnn/core'
import { currentLocale } from '@tnn/i18n'
import { DateField, DateTimeField, MoneyField, RemoteMultiSelect, RemoteSelect, SuggestInput, TimeField } from '@tnn/ui'
import type { TFunction } from 'i18next'
import { type ReactNode, useId } from 'react'
import { useTranslation } from 'react-i18next'
import { useOptionsLoader, useSuggestLoader } from '../api/resources'
import { FileUpload } from '../fields/file'

export interface FieldInputProps {
  resource: Resource
  field: ResolvedField
  // biome-ignore lint/suspicious/noExplicitAny: form values are resource-shaped
  form: UseFormReturnType<any>
  t: TFunction
  locale: Locale
  readOnly?: boolean
  /** A line under the input (a custom field's hint), above its error */
  description?: ReactNode
}

/**
 * Form labels are plain text, not `<label htmlFor>`: clicking one does not focus or open its
 * input. The input still takes its accessible name from the label, via `aria-labelledby`.
 * Spread onto any Mantine input in a form, custom fields included.
 */
export function useFieldLabel() {
  const id = useId()
  return { id, labelProps: { labelElement: 'div' as const }, 'aria-labelledby': `${id}-label` }
}

/** A description under its input, as a hint to what is typed in it */
const descriptionUnder: ('label' | 'input' | 'description' | 'error')[] = ['label', 'input', 'description', 'error']

/**
 * Picks the input for a field from its definition. Every form in every app
 * goes through here, so a new field type only needs adding once.
 */
export function FieldInput({ resource, field, form, t, locale, readOnly, description }: FieldInputProps) {
  const input = form.getInputProps(field.name)
  const labelling = useFieldLabel()
  const common = {
    ...labelling,
    label: t(field.label),
    withAsterisk: field.required && !readOnly,
    // read-only rather than disabled: the text stays full contrast and can be selected and copied
    readOnly,
    error: input.error,
    onFocus: input.onFocus,
    onBlur: input.onBlur,
    ...(description ? { description, inputWrapperOrder: descriptionUnder } : {}),
  }
  const options = () =>
    (field.options ?? []).map((value) => ({ value, label: t(labels.option(resource, field.name, value), { defaultValue: value }) }))

  if (field.ref && field.type === 'multiselect') {
    return <RefMulti field={field} input={input} common={common} />
  }
  if (field.ref) {
    return <RefSingle field={field} input={input} common={common} placeholder={readOnly ? undefined : t('form.searchPlaceholder')} />
  }
  if (field.suggest) {
    return <Suggest resource={resource} field={field} input={input} common={common} hint={t('form.suggestHint')} />
  }

  switch (field.type) {
    case 'textarea':
      return <Textarea {...common} autosize minRows={2} maxRows={8} value={input.value ?? ''} onChange={input.onChange} />
    case 'number':
    case 'integer':
      return (
        <NumberInput
          {...common}
          value={input.value ?? ''}
          onChange={input.onChange}
          min={field.min}
          max={field.max}
          allowDecimal={field.type === 'number'}
          thousandSeparator=","
          hideControls
          styles={{ input: { fontVariantNumeric: 'tabular-nums' } }}
        />
      )
    case 'money':
      return <MoneyField {...common} unit={t('units.baht')} value={input.value ?? ''} onChange={input.onChange} />
    case 'percent':
      return <NumberInput {...common} value={input.value ?? ''} onChange={input.onChange} min={0} max={100} suffix="%" hideControls />
    case 'date':
      return <DateField {...common} locale={locale} value={input.value ?? null} onChange={input.onChange} />
    case 'datetime':
      return (
        <DateTimeField
          {...common}
          locale={locale}
          value={input.value ?? null}
          onChange={(v) => input.onChange(v ? new Date(v).toISOString() : null)}
        />
      )
    case 'time':
      return <TimeField {...common} value={input.value ?? ''} onChange={(e) => input.onChange(e.currentTarget.value)} />
    case 'select':
      return <Select {...common} data={options()} value={input.value ?? null} onChange={input.onChange} allowDeselect={!field.required} />
    case 'multiselect':
      return <MultiSelect {...common} data={options()} value={input.value ?? []} onChange={input.onChange} />
    case 'boolean':
      return (
        <Switch
          label={common.label}
          description={description}
          disabled={readOnly}
          checked={!!input.value}
          onChange={(e) => input.onChange(e.currentTarget.checked)}
          mt="md"
        />
      )
    case 'tags':
      return <TagsInput {...common} value={input.value ?? []} onChange={input.onChange} />
    case 'file':
      return (
        <FileUpload
          field={field.name}
          label={common.label}
          withAsterisk={common.withAsterisk}
          readOnly={readOnly}
          error={common.error}
          value={input.value || null}
          onChange={input.onChange}
        />
      )
    case 'email':
      return <TextInput {...common} type="email" inputMode="email" value={input.value ?? ''} onChange={input.onChange} />
    case 'phone':
      return <TextInput {...common} type="tel" inputMode="tel" value={input.value ?? ''} onChange={input.onChange} />
    case 'taxId':
      return <TextInput {...common} inputMode="numeric" maxLength={13} value={input.value ?? ''} onChange={input.onChange} />
    case 'password':
      return <PasswordInput {...common} autoComplete="new-password" value={input.value ?? ''} onChange={input.onChange} />
    default:
      return <TextInput {...common} maxLength={field.maxLength} value={input.value ?? ''} onChange={input.onChange} />
  }
}

// biome-ignore lint/suspicious/noExplicitAny: Mantine form input props
type InputBag = any

function RefSingle({
  field,
  input,
  common,
  placeholder,
}: {
  field: ResolvedField
  input: InputBag
  common: InputBag
  placeholder?: string
}) {
  const loader = useOptionsLoader(field.ref?.resource ?? '', field.ref?.where)
  return (
    <RemoteSelect
      {...common}
      placeholder={placeholder}
      value={input.value ?? null}
      onChange={(v) => input.onChange(v)}
      load={loader.load}
      resolve={loader.resolve}
    />
  )
}

function RefMulti({ field, input, common }: { field: ResolvedField; input: InputBag; common: InputBag }) {
  const loader = useOptionsLoader(field.ref?.resource ?? '', field.ref?.where)
  return (
    <RemoteMultiSelect
      {...common}
      value={input.value ?? []}
      onChange={(v) => input.onChange(v)}
      load={loader.load}
      resolve={loader.resolve}
    />
  )
}

function Suggest({
  resource,
  field,
  input,
  common,
  hint,
}: {
  resource: Resource
  field: ResolvedField
  input: InputBag
  common: InputBag
  hint: string
}) {
  const load = useSuggestLoader(resource.key, field.name)
  // The hint sits in the placeholder, so a row with an autocomplete field lines up with its neighbours
  return <SuggestInput {...common} placeholder={hint} value={input.value ?? ''} onChange={(v) => input.onChange(v)} load={load} />
}

export interface ShownWhenProps extends Omit<FieldInputProps, 'locale'> {
  /** Whether the field applies to what is being typed (a company's branch, an individual's ID card) */
  show: (values: Record<string, unknown>) => boolean
}

/** For a `form.custom` field: the ordinary input, drawn only while it applies (otherwise the row is left out) */
export function ShownWhen({ show, ...props }: ShownWhenProps) {
  const { i18n } = useTranslation()
  if (!show(props.form.values as Record<string, unknown>)) return null
  return <FieldInput {...props} locale={currentLocale(i18n)} />
}
